import React, { useState, useEffect } from 'react'
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { PAGINAS_PERMISSAO } from '../lib/permissoes'

// Páginas só de Super Admin (não configuráveis na matriz de permissões).
const MENU_SUPERUSER = [
  { to: '/admin/configuracoes-super',       icon: '🛡️', label: 'Config Admin' },
  { to: '/admin/managers',                  icon: '🔑', label: 'Managers'       },
  { to: '/admin/permissoes',                icon: '🔒', label: 'Permissões'     },
]

const LABEL_ROLE = {
  superuser:   'Super Admin',
  manager:     'Manager',
}

export default function AdminLayout() {
  const { utilizador, perfil, role, isSuper, podeAceder, logout } = useAuth()
  const { tenant } = useTenant()
  const [menuAberto, setMenuAberto] = useState(false)
  const location = useLocation()

  useEffect(() => {
    setMenuAberto(false)
  }, [location.pathname])

  // Tranca o scroll do fundo enquanto o menu lateral está aberto (mobile).
  // O menu (position:fixed, overflow-y:auto) faz scroll dentro de si próprio.
  useEffect(() => {
    if (!menuAberto) return
    document.body.classList.add('menu-aberto')
    const onKey = (e) => { if (e.key === 'Escape') setMenuAberto(false) }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('menu-aberto')
      window.removeEventListener('keydown', onKey)
    }
  }, [menuAberto])

  const nomeVisivel = perfil?.profissionais?.nome ?? perfil?.nome ?? utilizador?.email ?? '—'
  const menu = [
    ...PAGINAS_PERMISSAO.filter(p => podeAceder(p.chave)),
    ...(isSuper ? MENU_SUPERUSER : []),
  ]

  return (
    <div className="admin-shell">
      {menuAberto && (
        <div className="admin-backdrop" onClick={() => setMenuAberto(false)} />
      )}

      <aside className={`admin-sidebar${menuAberto ? ' aberta' : ''}`}>
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ color: 'white', fontFamily: 'var(--font-base)', fontSize: '17px' }}>Backoffice</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', marginTop: '3px' }}>
            {tenant?.nome ?? 'Backoffice'}
          </div>
        </div>

        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {menu.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '11px 20px',
                color: isActive ? 'white' : 'rgba(255,255,255,0.68)',
                background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                textDecoration: 'none',
                fontSize: '14px',
                borderLeft: `3px solid ${isActive ? 'white' : 'transparent'}`,
                transition: 'all 0.15s',
              })}
            >
              <span role="img" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ color: 'white', fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nomeVisivel}
            </div>
            {role && LABEL_ROLE[role] && (
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '2px' }}>
                {LABEL_ROLE[role]}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.85)', fontSize: '13px', textDecoration: 'none' }}>
              ← Página Principal
            </Link>
            <button
              onClick={logout}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '13px',
                cursor: 'pointer',
                padding: '0',
                textAlign: 'left',
              }}
            >
              Terminar sessão
            </button>
          </div>
        </div>
      </aside>

      <div className="admin-content">
        {/* Barra superior — só visível em mobile (ver CSS) */}
        <header className="admin-topbar">
          <button
            type="button"
            className="admin-burger"
            aria-label="Abrir menu"
            onClick={() => setMenuAberto(true)}
          >
            ☰
          </button>
          <span className="admin-topbar-marca">{tenant?.nome || 'Backoffice'}</span>
        </header>

        <Outlet />
      </div>
    </div>
  )
}
